/*
===============================================================================================================
                                 Copyright 2026-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import SimpleButton from '@/components/button-simple';
import InlineEditableText from '@/components/inline-editable-text';
import { LocaleContext } from '@/contexts/locale-context';
import { unEscapeCustomText } from '@/modules/page-customisation/utilities';

const TITLE_FIELD_ID = 'title';
const DEFAULT_TITLE_KEY = 'customisation.giftReturnsSectionTitlePlaceholder';
const DEFAULT_BUTTON_KEY = 'customisation.giftReturnsSectionButtonPlaceholder';

export default function EditableGiftReturnsBlock({
  inputSelectorId,
  customTextValues,
  editEnabled,
  onTitleChange,
  titleDefaultResetValue = DEFAULT_TITLE_KEY,
  buttonText = DEFAULT_BUTTON_KEY,
}) {
  const { translate } = useContext(LocaleContext);

  const onTitleFieldChange = (value) => {
    const valueToUse = unEscapeCustomText(value);
    onTitleChange?.(valueToUse);
  };

  return (
    <div className="gift-returns-wrapper">
      <div className="gift-returns-title">
        <InlineEditableText
          inputSelectorId={`${inputSelectorId}-title`}
          validationRules={{
            onSubmit: ['nonEmpty'],
            onChange: ['nonEmpty'],
            tooLongError: translate('validation.invalidValue'),
          }}
          maxLength={120}
          defaultValue={customTextValues?.[TITLE_FIELD_ID] || ''}
          defaultResetValue={titleDefaultResetValue}
          textAreaMode
          readOnly={!editEnabled}
          fontSize="var(--font-size-sm)"
          fontWeight="var(--font-weight-body)"
          centered
          inputTextAlignment="center"
          onChange={onTitleFieldChange}
        />
      </div>
      <div className="gift-returns-button">
        <SimpleButton
          inputSelectorId={`${inputSelectorId}-button`}
          inputHeight={36}
          inputWidth={240}
          fontFamily="var(--font-family-body-sans), Arial, Helvetica, sans-serif"
          fontSize="--font-size-H3"
          fontWeight="--font-weight-H1"
          hasMargin={false}
          disabled
          readOnly
          centered
          backgroundColor="#F6F6F6"
          textColor="--color-primary-dark"
          borderColor="--color-primary-dark"
        >
          {translate(buttonText)}
        </SimpleButton>
      </div>
      <style jsx>
        {`
          .gift-returns-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            row-gap: 10px;
            margin: 0px -40px;
            width: calc(100% + 80px);
            padding: 24px 40px;
            background: #F6F6F6;
          }

          .gift-returns-title {
            line-height: 1.4;
            max-width: 420px;
            width: 100%;
          }

          .gift-returns-button {
            display: flex;
            justify-content: center;
          }
        `}
      </style>
    </div>
  );
}

EditableGiftReturnsBlock.defaultProps = {
  inputSelectorId: 'gift-returns-block',
  customTextValues: null,
  editEnabled: false,
  onTitleChange: null,
  titleDefaultResetValue: DEFAULT_TITLE_KEY,
  buttonText: DEFAULT_BUTTON_KEY,
};

EditableGiftReturnsBlock.propTypes = {
  inputSelectorId: PropTypes.string,
  customTextValues: PropTypes.shape({
    title: PropTypes.string,
  }),
  editEnabled: PropTypes.bool,
  onTitleChange: PropTypes.func,
  titleDefaultResetValue: PropTypes.string,
  buttonText: PropTypes.string,
};
